import { create } from "zustand"

import type {
	AiChatFeedbackSentiment,
	AiChatMessage,
	ContentRewriteTarget,
	CopilotScope,
} from "@productlane/lib/src/ai"
import type { ChatInputTextPaste } from "@productlane/ui/src/components/chat-input-text-paste"
import { copilotScopeKey } from "@productlane/lib/src/ai"

import type { EnabledMcpSectionIds } from "./sidebar-ai-chat-mcp"
import { useAuthState } from "@/lib/states/auth-state"
import { mergeHydratedHistory } from "./sidebar-ai-chat-hydration"

interface PendingContentRewrite {
	target: ContentRewriteTarget
	html: string
}

export interface CachedScopeChat {
	sessionId: string | null
	messages: AiChatMessage[]
	requireFreshSession: boolean
	isLoading: boolean
	enabledMcpSectionIds: EnabledMcpSectionIds
	pageContextDismissed: boolean
}

interface SidebarAiChatState {
	sessionId: string | null
	scope: CopilotScope | null
	messages: AiChatMessage[] | null
	messageValue: string
	// Long pastes waiting in the composer as pills. They fold into the message
	// on submit, so they live next to the draft text rather than in it.
	pastedTexts: ChatInputTextPaste[]
	isLoading: boolean
	// Set when the user clicks Apply on a content-rewrite card. The matching
	// editor (composer / article / changelog) consumes and clears it.
	pendingContentRewrite: PendingContentRewrite | null
	pendingPrompt: string | null
	// Set when the user clicks "New chat" so the next outgoing message
	// allocates a fresh session instead of reusing the most recent one.
	requireFreshSession: boolean
	// Set after a postgres approval succeeds. The sync layer picks this up
	// and POSTs a follow-up turn that asks the model to interpret the
	// query result. Cleared when the follow-up stream starts.
	pendingPostgresContinuation: string | null
	// Set after the model switched MCP connectors on. The sync layer POSTs a
	// follow-up turn so the newly bound customMcp_* tools can run.
	enabledMcpSectionIds: EnabledMcpSectionIds
	// True while a persisted conversation is on its way in. The composer is
	// already usable; the transcript area stays blank rather than flashing the
	// empty state before the history lands.
	isHydratingHistory: boolean
	// In-memory chat per scope so switching docs/changelogs restores the
	// right conversation (including an in-flight answer) without a round trip.
	cachedScopeChats: Record<string, CachedScopeChat>
	// The user dropped the on-screen article or changelog from the context, so
	// the next turn goes out without it.
	pageContextDismissed: boolean

	setSessionId: (sessionId: string | null) => void
	setMessages: (messages: AiChatMessage[] | null) => void
	setMessageValue: (value: string) => void
	setPastedTexts: (pastes: ChatInputTextPaste[]) => void
	setIsLoading: (isLoading: boolean) => void
	setPendingContentRewrite: (rewrite: PendingContentRewrite | null) => void
	setPendingPrompt: (prompt: string | null) => void
	setRequireFreshSession: (value: boolean) => void
	setPendingPostgresContinuation: (pendingActionId: string | null) => void
	setEnabledMcpSectionIds: (ids: EnabledMcpSectionIds) => void
	setIsHydratingHistory: (value: boolean) => void
	setPageContextDismissed: (value: boolean) => void
	setMessageFeedback: (
		messageId: string,
		sentiment: AiChatFeedbackSentiment | null,
	) => void
	hydrateHistory: (history: AiChatMessage[]) => void
	switchSession: (sessionId: string, messages: AiChatMessage[]) => void
	startNewChat: () => void
	// Returns true when the new scope was restored from the in-memory cache.
	resetForScope: (scope: CopilotScope) => boolean
	patchCachedScopeChat: (
		scopeKey: string,
		patch: Partial<CachedScopeChat> & { messages?: AiChatMessage[] },
	) => void
	initMessages: () => void
	reset: () => void
}

export const useSidebarAiChatState = create<SidebarAiChatState>()(
	(set, get) => ({
		sessionId: null,
		scope: null,
		messages: null,
		messageValue: "",
		pastedTexts: [],
		isLoading: false,
		pendingContentRewrite: null,
		pendingPrompt: null,
		requireFreshSession: false,
		pendingPostgresContinuation: null,
		enabledMcpSectionIds: null,
		isHydratingHistory: false,
		cachedScopeChats: {},
		pageContextDismissed: false,

		setSessionId: (sessionId) => set({ sessionId }),
		setMessages: (messages) => set({ messages }),
		setMessageValue: (value) => set({ messageValue: value }),
		setPastedTexts: (pastes) => set({ pastedTexts: pastes }),
		setIsLoading: (isLoading) => set({ isLoading }),
		setPendingContentRewrite: (rewrite) =>
			set({ pendingContentRewrite: rewrite }),
		setPendingPrompt: (prompt) => set({ pendingPrompt: prompt }),
		setRequireFreshSession: (value) => set({ requireFreshSession: value }),
		setPendingPostgresContinuation: (pendingActionId) =>
			set({ pendingPostgresContinuation: pendingActionId }),
		setEnabledMcpSectionIds: (ids) => set({ enabledMcpSectionIds: ids }),
		setIsHydratingHistory: (value) => set({ isHydratingHistory: value }),
		setPageContextDismissed: (value) => set({ pageContextDismissed: value }),

		setMessageFeedback: (messageId, sentiment) => {
			const { messages } = get()
			if (!messages) return
			set({
				messages: messages.map((m) =>
					m.id === messageId
						? { ...m, feedback: sentiment ? { sentiment } : undefined }
						: m,
				),
			})
		},

		hydrateHistory: (history) => {
			const { messages } = get()
			set({
				messages: mergeHydratedHistory(history, messages),
				isHydratingHistory: false,
			})
		},

		switchSession: (sessionId, messages) => {
			set({
				sessionId,
				messages,
				isLoading: false,
				messageValue: "",
				pastedTexts: [],
				requireFreshSession: false,
				isHydratingHistory: false,
				enabledMcpSectionIds: null,
			})
		},

		startNewChat: () => {
			const { scope, cachedScopeChats } = get()
			const nextCache = { ...cachedScopeChats }
			if (scope) {
				delete nextCache[copilotScopeKey(scope)]
			}
			set({
				sessionId: null,
				messages: [],
				isLoading: false,
				messageValue: "",
				pastedTexts: [],
				requireFreshSession: true,
				isHydratingHistory: false,
				enabledMcpSectionIds: null,
				cachedScopeChats: nextCache,
				pageContextDismissed: false,
			})
		},

		resetForScope: (scope) => {
			const state = get()
			const nextCache = { ...state.cachedScopeChats }
			if (state.scope && state.messages !== null) {
				nextCache[copilotScopeKey(state.scope)] = {
					sessionId: state.sessionId,
					messages: state.messages,
					requireFreshSession: state.requireFreshSession,
					isLoading: state.isLoading,
					enabledMcpSectionIds: state.enabledMcpSectionIds,
					pageContextDismissed: state.pageContextDismissed,
				}
			}

			const cached = nextCache[copilotScopeKey(scope)]
			if (cached) {
				set({
					sessionId: cached.sessionId,
					scope,
					messages: cached.messages,
					messageValue: "",
					pastedTexts: [],
					isLoading: cached.isLoading,
					pendingContentRewrite: null,
					pendingPrompt: null,
					requireFreshSession: cached.requireFreshSession,
					pendingPostgresContinuation: null,
					enabledMcpSectionIds: cached.enabledMcpSectionIds,
					isHydratingHistory: false,
					cachedScopeChats: nextCache,
					pageContextDismissed: cached.pageContextDismissed,
				})
				return true
			}

			set({
				sessionId: null,
				scope,
				messages: null,
				messageValue: "",
				pastedTexts: [],
				isLoading: false,
				pendingContentRewrite: null,
				pendingPrompt: null,
				requireFreshSession: false,
				pendingPostgresContinuation: null,
				enabledMcpSectionIds: null,
				isHydratingHistory: false,
				cachedScopeChats: nextCache,
				pageContextDismissed: false,
			})
			return false
		},

		patchCachedScopeChat: (scopeKey, patch) => {
			const state = get()
			if (state.scope && copilotScopeKey(state.scope) === scopeKey) {
				set({
					...(patch.sessionId !== undefined
						? { sessionId: patch.sessionId }
						: {}),
					...(patch.messages !== undefined ? { messages: patch.messages } : {}),
					...(patch.requireFreshSession !== undefined
						? { requireFreshSession: patch.requireFreshSession }
						: {}),
					...(patch.isLoading !== undefined
						? { isLoading: patch.isLoading }
						: {}),
					...(patch.enabledMcpSectionIds !== undefined
						? { enabledMcpSectionIds: patch.enabledMcpSectionIds }
						: {}),
					...(patch.pageContextDismissed !== undefined
						? { pageContextDismissed: patch.pageContextDismissed }
						: {}),
				})
				return
			}

			const existing = state.cachedScopeChats[scopeKey]
			set({
				cachedScopeChats: {
					...state.cachedScopeChats,
					[scopeKey]: {
						sessionId:
							patch.sessionId !== undefined
								? patch.sessionId
								: (existing?.sessionId ?? null),
						messages: patch.messages ?? existing?.messages ?? [],
						requireFreshSession:
							patch.requireFreshSession ??
							existing?.requireFreshSession ??
							false,
						isLoading: patch.isLoading ?? existing?.isLoading ?? false,
						enabledMcpSectionIds:
							patch.enabledMcpSectionIds !== undefined
								? patch.enabledMcpSectionIds
								: (existing?.enabledMcpSectionIds ?? null),
						pageContextDismissed:
							patch.pageContextDismissed ??
							existing?.pageContextDismissed ??
							false,
					},
				},
			})
		},

		initMessages: () => {
			const { messages } = get()
			if (messages === null) {
				set({ messages: [] })
			}
		},

		reset: () =>
			set({
				sessionId: null,
				messages: null,
				messageValue: "",
				pastedTexts: [],
				isLoading: false,
				pendingContentRewrite: null,
				pendingPrompt: null,
				requireFreshSession: false,
				pendingPostgresContinuation: null,
				enabledMcpSectionIds: null,
				isHydratingHistory: false,
				pageContextDismissed: false,
			}),
	}),
)

// Creating a workspace swaps it without a reload, so chats from the old one must not linger.
useAuthState.subscribe((state, previous) => {
	if (state.data?.workspaceId === previous.data?.workspaceId) return
	useSidebarAiChatState.getState().reset()
	useSidebarAiChatState.setState({ cachedScopeChats: {} })
})

export { copilotScopeKey }
